import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useProvider } from '../lib/provider';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../hooks/useTranslation';
import { isToday } from '../lib/utils';
import { NestQAvatar } from '../components/NestQAvatar';
import {
    ChevronLeft,
    Trophy,
    Star,
    History,
    CheckCircle2,
    XCircle,
    Trash2,
    Gift,
    Plus,
    Check,
    Clock,
    Circle,
    ChevronDown
} from 'lucide-react';

export const ParentKidDetails: React.FC = () => {
    const { kidId } = useParams<{ kidId: string }>();
    const navigate = useNavigate();
    const provider = useProvider();
    const { session } = useAuth();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const [showBonusForm, setShowBonusForm] = useState(false);
    const [bonusPoints, setBonusPoints] = useState(10);
    const [bonusReason, setBonusReason] = useState('');
    const [showSwitchMenu, setShowSwitchMenu] = useState(false);
    const switchMenuRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (switchMenuRef.current && !switchMenuRef.current.contains(event.target as Node)) {
                setShowSwitchMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const { data: members } = useQuery({
        queryKey: ['members', session.user?.household_id],
        queryFn: () => provider.getMembers(session.user!.household_id),
        enabled: !!session.user?.household_id,
    });

    // Find kid by name (from URL) or ID (fallback for backward compatibility)
    const kid = React.useMemo(() => {
        if (!members) return null;
        
        // Try to find by name first (URL-friendly format)
        const decodedName = decodeURIComponent(kidId || '').replace(/-/g, ' ');
        const kidByName = members.find(m => 
            m.role === 'kid' && 
            m.display_name.toLowerCase() === decodedName.toLowerCase()
        );
        
        if (kidByName) return kidByName;
        
        // Fallback to finding by ID (for backward compatibility)
        return members.find(m => m.role === 'kid' && m.user_id === kidId);
    }, [members, kidId]);

    const actualKidId = kid?.user_id;

    // Real-time subscription for quest completions
    useEffect(() => {
        if (!session.user?.household_id || !provider.getSupabaseClient || !actualKidId) return;

        const supabase = provider.getSupabaseClient();
        
        // Subscribe to quest completions for this household
        const subscription = supabase
            .channel('quest_completions')
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public',
                    table: 'quest_completions',
                    filter: `kid_user_id=eq.${actualKidId}`
                },
                (payload: any) => {
                    console.log('Quest completion change detected:', payload);
                    
                    // Refresh relevant queries when completions change
                    queryClient.invalidateQueries({ queryKey: ['kid-completions', actualKidId] });
                    queryClient.invalidateQueries({ queryKey: ['kid-history', actualKidId] });
                    queryClient.invalidateQueries({ queryKey: ['balance', actualKidId] });
                    queryClient.invalidateQueries({ queryKey: ['pending-completions'] });
                    queryClient.invalidateQueries({ queryKey: ['kid-quests', actualKidId] });
                }
            )
            .subscribe();

        return () => {
            subscription.unsubscribe();
        };
    }, [session.user?.household_id, actualKidId, provider, queryClient]);

    const { data: balance, isLoading: loadingBalance } = useQuery({
        queryKey: ['balance', actualKidId],
        queryFn: () => provider.getBalance(actualKidId!),
        enabled: !!actualKidId,
    });

    const { data: assignedQuests, isLoading: loadingQuests } = useQuery({
        queryKey: ['kid-quests', actualKidId],
        queryFn: () => provider.getKidQuests(actualKidId!),
        enabled: !!actualKidId,
    });

    const { data: completions, isLoading: loadingCompletions } = useQuery({
        queryKey: ['kid-completions', actualKidId],
        queryFn: () => provider.getKidCompletions(actualKidId!),
        enabled: !!actualKidId,
    });

    const { data: history, isLoading: loadingHistory } = useQuery({
        queryKey: ['kid-history', actualKidId],
        queryFn: () => provider.getKidHistory(actualKidId!),
        enabled: !!actualKidId,
    });

    const approvedOneTimeQuestIds = new Set(
        (completions || [])
            .filter(c => c.status === 'approved' && c.quest.frequency === 'one_time')
            .map(c => c.quest_id)
    );

    const activeAssignedQuests = (assignedQuests || []).filter(q => {
        if (approvedOneTimeQuestIds.has(q.id)) return false;

        if (q.frequency === 'daily') {
            const completedToday = (completions || []).some(c => 
                c.quest_id === q.id && 
                (c.status === 'approved' || c.status === 'pending') && 
                isToday(c.completed_at)
            );
            return !completedToday;
        }

        if (q.frequency === 'weekly') {
            if (typeof q.weekly_day !== 'number') return false;
            if (new Date().getDay() !== q.weekly_day) return false;

            const completedToday = (completions || []).some(c =>
                c.quest_id === q.id &&
                (c.status === 'approved' || c.status === 'pending') &&
                isToday(c.completed_at)
            );
            return !completedToday;
        }

        if (q.frequency === 'one_time') {
            if (!q.due_date) return false;
            return isToday(q.due_date);
        }

        return false;
    });
    
    // Also include daily quests that should show for tomorrow (matching KidHome logic)
    const completedDailyQuestsForTomorrow = (assignedQuests || []).filter(q => {
        if (q.frequency !== 'daily') return false;
        if (approvedOneTimeQuestIds.has(q.id)) return false;
        
        // Include all daily quests for tomorrow (both completed and uncompleted) - matching KidHome logic
        return true;
    });
    
    // Combine quests: always show 5 total, prioritizing today's quests - matching KidHome logic
    const allAssignedQuests: any[] = [];
    const targetCount = 5;
    
    // Add today's quests first
    allAssignedQuests.push(...activeAssignedQuests.map(quest => ({
        ...quest,
        isDuplicate: false
    })));
    
    // If we need more quests, add tomorrow's quests
    if (allAssignedQuests.length < targetCount) {
        const remainingNeeded = targetCount - allAssignedQuests.length;
        const tomorrowsToAdd = completedDailyQuestsForTomorrow
            .filter(quest => !allAssignedQuests.some(fq => fq.id === quest.id))
            .slice(0, remainingNeeded);
        
        allAssignedQuests.push(...tomorrowsToAdd.map(quest => ({
            ...quest,
            isDuplicate: true
        })));
    }
    
    const finalQuests = allAssignedQuests.slice(0, targetCount);

    const approveCompletion = useMutation({
        mutationFn: (id: string) => provider.approveCompletion(id, session.user!.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['kid-completions', actualKidId] });
            queryClient.invalidateQueries({ queryKey: ['kid-history', actualKidId] });
            queryClient.invalidateQueries({ queryKey: ['balance', actualKidId] });
            queryClient.invalidateQueries({ queryKey: ['pending-completions'] });
        },
    });

    const rejectCompletion = useMutation({
        mutationFn: (id: string) => provider.rejectCompletion(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['kid-completions', actualKidId] });
            queryClient.invalidateQueries({ queryKey: ['kid-history', actualKidId] });
            queryClient.invalidateQueries({ queryKey: ['pending-completions'] });
        },
    });

    const deleteCompletion = useMutation({
        mutationFn: (id: string) => provider.deleteCompletion(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['kid-completions', actualKidId] });
            queryClient.invalidateQueries({ queryKey: ['kid-history', actualKidId] });
            queryClient.invalidateQueries({ queryKey: ['balance', actualKidId] });
        },
        onError: (err: any) => {
            alert(err?.message || 'Failed to delete activity entry');
        },
    });

    const deleteBonus = useMutation({
        mutationFn: (ledgerEntryId: string) => provider.deleteManualPoints(ledgerEntryId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['balance', actualKidId] });
            queryClient.invalidateQueries({ queryKey: ['kid-history', actualKidId] });
        },
        onError: (err: any) => {
            alert(err?.message || 'Failed to delete bonus entry');
        },
    });

    const addBonusPoints = useMutation({
        mutationFn: () => provider.addManualPoints(actualKidId!, bonusPoints, bonusReason, session.user!.id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['balance', actualKidId] });
            queryClient.invalidateQueries({ queryKey: ['kid-completions', actualKidId] });
            queryClient.invalidateQueries({ queryKey: ['kid-history', actualKidId] });
            setShowBonusForm(false);
            setBonusPoints(10);
            setBonusReason('');
        },
        onError: (err: any) => {
            alert(err?.message || 'Failed to add bonus points');
        },
    });

    const completeQuest = useMutation({
        mutationFn: async (questId: string) => {
            if (!actualKidId) {
                throw new Error('Kid ID not found');
            }
            
            // First complete the quest
            const completion = await provider.completeQuest({
                quest_id: questId,
                kid_user_id: actualKidId,
            });
            
            // Then auto-approve it since parent is completing it
            await provider.approveCompletion(completion.id, session.user!.id);
            
            return completion;
        },
        onSuccess: () => {
            // Invalidate both specific and general query keys to match KidHome
            queryClient.invalidateQueries({ queryKey: ['kid-quests', actualKidId] });
            queryClient.invalidateQueries({ queryKey: ['kid-completions', actualKidId] });
            queryClient.invalidateQueries({ queryKey: ['kid-quests'] });
            queryClient.invalidateQueries({ queryKey: ['kid-completions'] });
            queryClient.invalidateQueries({ queryKey: ['balance', actualKidId] });
            queryClient.invalidateQueries({ queryKey: ['balance'] });
            queryClient.invalidateQueries({ queryKey: ['pending-completions'] });
        },
        onError: (err: any) => {
            console.error('Complete quest error:', err);
            alert(err?.message || 'Failed to complete quest');
        },
    });

    const getFrequencyLabel = (freq: string) => {
        if (freq === 'daily') return t.parentQuests.daily;
        if (freq === 'weekly') return t.parentQuests.weekly;
        return t.parentQuests.oneTime;
    };

    // Helper functions from KidHome
    const getDailyTimeLabel = (quest: any) => {
        if (quest.frequency !== 'daily') return null;
        if (typeof quest.daily_start_hour !== 'number' || typeof quest.daily_end_hour !== 'number') return null;
        return `${quest.daily_start_hour}:00-${quest.daily_end_hour}:00`;
    };

    // Check if quest is within its allowed time window
    const isWithinDailyWindow = (quest: any) => {
        if (quest.frequency !== 'daily') return true;
        if (typeof quest.daily_start_hour !== 'number' || typeof quest.daily_end_hour !== 'number') return true;
        const now = new Date();
        const hour = now.getHours();
        return hour >= quest.daily_start_hour && hour < quest.daily_end_hour;
    };

    // Only disable the button if it's a tomorrow quest (marked as duplicate) or outside time window
    const isQuestDisabled = (quest: any & { isDuplicate?: boolean }) => {
        // Disable tomorrow quests - can't complete them today
        if (quest.isDuplicate) return true;
        
        // Disable if mutation is pending
        if (completeQuest.isPending) return true;
        
        // Disable today's quests if outside their time window
        if (!quest.isDuplicate && !isWithinDailyWindow(quest)) return true;
        
        return false;
    };

    const getQuestDayLabel = (quest: any & { isDuplicate?: boolean }) => {
        try {
            // If this is marked as duplicate, show as tomorrow
            if (quest.isDuplicate) {
                return t.common.tomorrow || 'Tomorrow';
            }
            
            if (quest.frequency === 'daily') {
                if (typeof quest.daily_end_hour === 'number') {
                    const now = new Date();
                    const hour = now.getHours();
                    if (hour >= quest.daily_end_hour) return t.common.tomorrow || 'Tomorrow';
                }
                return t.common.today || 'Today';
            }

            if (quest.frequency === 'weekly' && typeof quest.weekly_day === 'number') {
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const currentDay = today.getDay();
                const diff = (quest.weekly_day - currentDay + 7) % 7;

                if (diff === 0) return t.common.today || 'Today';
                if (diff === 1) return t.common.tomorrow || 'Tomorrow';

                const next = new Date(today);
                next.setDate(next.getDate() + diff);
                return next.toLocaleDateString('en-US', { weekday: 'short' });
            }

            if (quest.frequency === 'one_time') {
                if (quest.due_date) {
                    return new Date(quest.due_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                }

                if (quest.assignment?.created_at) {
                    return new Date(quest.assignment.created_at).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                }
            }

            return null;
        } catch (error) {
            console.error('Error getting quest day label:', error);
            return null;
        }
    };

    // Sort active quests: daily quests by start hour, then weekly, then one-time by points
    const sortedActiveQuests = React.useMemo(() => {
        return finalQuests?.sort((a, b) => {
            // Daily quests first, sorted by start hour
            if (a.frequency === 'daily' && b.frequency === 'daily') {
                const aStart = typeof a.daily_start_hour === 'number' ? a.daily_start_hour : 0;
                const bStart = typeof b.daily_start_hour === 'number' ? b.daily_start_hour : 0;
                return aStart - bStart;
            }
            if (a.frequency === 'daily' && b.frequency !== 'daily') return -1;
            if (b.frequency === 'daily' && a.frequency !== 'daily') return 1;

            // Weekly quests next
            if (a.frequency === 'weekly' && b.frequency === 'weekly') {
                const today = new Date().getDay();
                const aDay = a.weekly_day ?? 1;
                const bDay = b.weekly_day ?? 1;
                let daysUntilA = aDay - today;
                let daysUntilB = bDay - today;
                if (daysUntilA < 0) daysUntilA += 7;
                if (daysUntilB < 0) daysUntilB += 7;
                return daysUntilA - daysUntilB;
            }
            if (a.frequency === 'weekly' && b.frequency !== 'daily' && b.frequency !== 'weekly') return -1;
            if (b.frequency === 'weekly' && a.frequency !== 'daily' && a.frequency !== 'weekly') return 1;

            // One-time quests last, sorted by points (highest first)
            return b.points - a.points;
        }) || [];
    }, [finalQuests, assignedQuests, completions]);

    // Group active quests by day (similar to KidHome)
    const groupedQuests = React.useMemo(() => {
        if (!sortedActiveQuests || sortedActiveQuests.length === 0) {
            return [];
        }
        
        const groups: Array<{ key: string; label: string; quests: any[] }> = [];
        
        // Group quests by their day label
        const questMap = new Map<string, any[]>();
        
        sortedActiveQuests.forEach(quest => {
            const dayLabel = getQuestDayLabel(quest) || 'OTHER';
            if (!questMap.has(dayLabel)) {
                questMap.set(dayLabel, []);
            }
            questMap.get(dayLabel)!.push(quest);
        });

        // Order groups: TODAY, TOMORROW, SUNDAY, then others alphabetically
        const order = ['Today', 'Danes', 'Tomorrow', 'Jutri', 'SUNDAY', 'Sun'];
        
        order.forEach(label => {
            const group = Array.from(questMap.entries()).find(([key]) => key === label || key.includes(label));
            if (group) {
                groups.push({
                    key: label,
                    label: group[0],
                    quests: group[1]
                });
                questMap.delete(group[0]);
            }
        });
        
        // Add remaining groups
        Array.from(questMap.entries()).forEach(([key, quests]) => {
            groups.push({
                key: key,
                label: key,
                quests
            });
        });

        // Calculate total points for each group
        return groups.map(group => ({
            ...group,
            totalPoints: group.quests.reduce((sum, quest) => sum + (quest.points || 0), 0)
        }));
    }, [sortedActiveQuests]);

    // Group history by date (TODAY, YESTERDAY, then specific dates)
    const groupedHistory = React.useMemo(() => {
        if (!history || history.length === 0) {
            return [];
        }

        const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const today = startOfDay(new Date());
        const dayDiff = (a: Date, b: Date) => {
            const msPerDay = 24 * 60 * 60 * 1000;
            return Math.round((startOfDay(a).getTime() - startOfDay(b).getTime()) / msPerDay);
        };

        const dateKeyFor = (date: Date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        };

        const dateLabelFor = (date: Date) => {
            const diff = dayDiff(date, today);
            if (diff === 0) return 'TODAY';
            if (diff === -1) return 'YESTERDAY';
            return date.toLocaleDateString();
        };

        const groups = new Map<string, { label: string; items: typeof history }>();
        for (const item of history) {
            const d = new Date(item.created_at);
            const key = dateKeyFor(d);
            const label = dateLabelFor(d);
            const existing = groups.get(key);
            if (existing) {
                existing.items.push(item);
            } else {
                groups.set(key, { label, items: [item] });
            }
        }

        // Sort items within each group by hour (most recent first)
        for (const group of groups.values()) {
            group.items.sort((a, b) => {
                const timeA = new Date(a.created_at).getTime();
                const timeB = new Date(b.created_at).getTime();
                return timeB - timeA; // Most recent first
            });
        }

        return Array.from(groups.entries())
            .sort((a, b) => b[0].localeCompare(a[0]))
            .map(([key, value]) => ({
                key,
                ...value,
                totalPoints: value.items.reduce((sum, item) => sum + (item.points || 0), 0),
            }));
    }, [history]);

    const isMissedCompletion = React.useCallback((completion: any) => {
        const quest = completion?.quest;
        if (!quest?.frequency || !completion?.completed_at) return false;

        const completedAt = new Date(completion.completed_at);
        if (Number.isNaN(completedAt.getTime())) return false;

        if (quest.frequency === 'daily') {
            if (typeof quest.daily_end_hour !== 'number') return false;

            const deadline = new Date(completedAt);
            deadline.setHours(quest.daily_end_hour, 0, 0, 0);
            return completedAt.getTime() > deadline.getTime();
        }

        if (quest.frequency === 'weekly') {
            if (typeof quest.weekly_day !== 'number') return false;

            if (completedAt.getDay() !== quest.weekly_day) return true;

            const deadline = new Date(completedAt);
            deadline.setHours(23, 59, 59, 999);
            return completedAt.getTime() > deadline.getTime();
        }

        if (quest.frequency === 'one_time') {
            if (!quest.due_date) return false;

            const due = new Date(quest.due_date);
            if (Number.isNaN(due.getTime())) return false;

            const deadline = new Date(due);
            deadline.setHours(23, 59, 59, 999);
            return completedAt.getTime() > deadline.getTime();
        }

        return false;
    }, []);

    const pendingApprovals = React.useMemo(() => {
        return (completions || []).filter(c => c.status === 'pending');
    }, [completions]);

    const missedUncompletedDaily = React.useMemo(() => {
        const now = new Date();
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        yesterday.setHours(0, 0, 0, 0);

        const startOfYesterday = new Date(yesterday);
        const startOfToday = new Date(now);
        startOfToday.setHours(0, 0, 0, 0);

        const isSameDay = (a: Date, b: Date) => (
            a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate()
        );

        const missed: Array<{
            id: string;
            quest: any;
            missed_deadline: string;
            missed_day_start: string;
            missed_day_end: string;
        }> = [];

        for (const q of assignedQuests || []) {
            if (q.frequency !== 'daily') continue;
            if (typeof q.daily_end_hour !== 'number') continue;

            const deadline = new Date(startOfYesterday);
            deadline.setHours(q.daily_end_hour, 0, 0, 0);
            if (now.getTime() <= deadline.getTime()) continue;

            const hadAnyCompletionYesterday = (completions || []).some(c => {
                if (c.quest_id !== q.id) return false;
                const completedAt = new Date(c.completed_at);
                if (Number.isNaN(completedAt.getTime())) return false;
                if (!isSameDay(completedAt, startOfYesterday)) return false;
                return true;
            });

            const wasApprovedAfterDeadline = (completions || []).some(c => {
                if (c.quest_id !== q.id) return false;
                if (c.status !== 'approved') return false;
                const completedAt = new Date(c.completed_at);
                if (Number.isNaN(completedAt.getTime())) return false;
                return completedAt.getTime() > deadline.getTime();
            });

            if (hadAnyCompletionYesterday || wasApprovedAfterDeadline) continue;

            missed.push({
                id: `${q.id}::missed::${startOfYesterday.toISOString().slice(0, 10)}`,
                quest: q,
                missed_deadline: deadline.toISOString(),
                missed_day_start: startOfYesterday.toISOString(),
                missed_day_end: startOfToday.toISOString(),
            });
        }

        return missed;
    }, [assignedQuests, completions]);

    const missedApprovals = React.useMemo(() => {
        return pendingApprovals.filter(c => isMissedCompletion(c));
    }, [isMissedCompletion, pendingApprovals]);

    const onTimeApprovals = React.useMemo(() => {
        return pendingApprovals.filter(c => !isMissedCompletion(c));
    }, [isMissedCompletion, pendingApprovals]);

    if (loadingBalance || loadingQuests || loadingCompletions || loadingHistory) {
        return <div className="p-8 text-center text-slate-500 font-bold">{t.common.loading}</div>;
    }

    if (!kid) {
        return (
            <div className="p-8 text-center">
                <p className="text-slate-500 mb-4">Kid not found.</p>
                <button
                    onClick={() => navigate('/parent/family')}
                    className="text-purple-600 font-bold flex items-center gap-1 mx-auto"
                >
                    <ChevronLeft size={20} /> {t.common.back}
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <button
                    onClick={() => navigate('/parent/family')}
                    className="flex items-center gap-1 text-slate-500 hover:text-purple-600 transition-colors font-bold"
                >
                    <ChevronLeft size={20} />
                    {t.common.back}
                </button>
            </div>

            {/* Hero Stats - matching KidHome style */}
            <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-3xl p-8 text-white shadow-xl shadow-purple-200">
                <div className="flex flex-col items-center gap-4">
                    <NestQAvatar size={275} />
                    <div className="flex flex-col items-center text-center">
                        {/* Kid name with dropdown switcher */}
                        {members && members.filter(m => m.role === 'kid').length > 1 ? (
                            <div className="relative" ref={switchMenuRef}>
                                <button
                                    onClick={() => setShowSwitchMenu(!showSwitchMenu)}
                                    className="flex items-center gap-2 text-3xl font-black mb-1 hover:opacity-80 transition-opacity"
                                >
                                    {kid.display_name}
                                    <ChevronDown size={24} className={`transition-transform ${showSwitchMenu ? 'rotate-180' : ''}`} />
                                </button>
                                
                                {showSwitchMenu && (
                                    <div className="absolute left-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50 min-w-[160px]">
                                        {members
                                            .filter(m => m.role === 'kid')
                                            .map(kidMember => (
                                                <button
                                                    key={kidMember.user_id}
                                                    onClick={() => {
                                                        setShowSwitchMenu(false);
                                                        navigate(`/parent/family/${encodeURIComponent(kidMember.display_name.toLowerCase().replace(/\s+/g, '-'))}`);
                                                    }}
                                                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                                        kidMember.user_id === actualKidId
                                                            ? 'bg-purple-50 text-purple-600 font-bold'
                                                            : 'text-slate-700 hover:bg-purple-50 hover:text-purple-600'
                                                    }`}
                                                >
                                                    {kidMember.display_name}
                                                    {kidMember.user_id === actualKidId && ' ✓'}
                                                </button>
                                            ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <h1 className="text-3xl font-black mb-1">{kid.display_name}</h1>
                        )}
                        <p className="opacity-80 font-medium">Quest progress and activity</p>
                    </div>
                    <div className="bg-white/20 backdrop-blur-md rounded-2xl p-4 flex items-center gap-3">
                        <Trophy className="text-amber-300" size={32} />
                        <div>
                            <div className="text-xs font-bold uppercase tracking-widest opacity-70">Total Points</div>
                            <div className="text-3xl font-black">{balance || 0}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bonus Points Section */}
            <section className="space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Gift className="text-purple-400" size={20} />
                        <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Bonus Points</h2>
                    </div>
                    {!showBonusForm && (
                        <button
                            onClick={() => setShowBonusForm(true)}
                            className="flex items-center gap-2 bg-purple-50 text-purple-600 px-4 py-2 rounded-xl border border-purple-100 hover:bg-purple-100 transition-colors font-bold"
                        >
                            <Plus size={16} />
                            Add Bonus
                        </button>
                    )}
                </div>

                {showBonusForm && (
                    <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Points</label>
                            <input
                                type="number"
                                min="1"
                                max="1000"
                                value={bonusPoints}
                                onChange={(e) => setBonusPoints(parseInt(e.target.value) || 0)}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">Reason</label>
                            <input
                                type="text"
                                value={bonusReason}
                                onChange={(e) => setBonusReason(e.target.value)}
                                placeholder={`Why does ${kid.display_name} deserve bonus points?`}
                                className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                            />
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => addBonusPoints.mutate()}
                                disabled={!bonusReason.trim() || addBonusPoints.isPending}
                                className="flex-1 bg-purple-600 text-white py-2 rounded-xl hover:bg-purple-700 transition-colors font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {addBonusPoints.isPending ? 'Adding...' : `Add ${bonusPoints} points`}
                            </button>
                            <button
                                onClick={() => {
                                    setShowBonusForm(false);
                                    setBonusPoints(10);
                                    setBonusReason('');
                                }}
                                className="px-4 py-2 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors font-bold"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </section>

            {/* Quests Section - matching KidHome layout */}
            <div className="space-y-4">
                <div className="flex items-center gap-2">
                    <Star className="text-amber-400 fill-amber-400" size={20} />
                    <h2 className="text-xl font-black text-slate-800 uppercase tracking-tight">Active Quests</h2>
                </div>
                
                {allAssignedQuests?.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 border-2 border-dashed border-indigo-100 text-center">
                        <p className="text-slate-400 font-bold">No quests assigned yet.</p>
                    </div>
                ) : groupedQuests.length > 0 ? (
                    <div className="space-y-6">
                        {groupedQuests.map((group) => (
                            <div key={group.key} className="space-y-3">
                                <div className="flex items-center justify-between px-1">
                                    <div className="flex items-center gap-2 flex-1">
                                        <div className="h-px bg-slate-200 flex-1"></div>
                                        <div className="text-sm font-black text-slate-600 uppercase tracking-widest px-3">
                                            {group.label}
                                        </div>
                                        <div className="h-px bg-slate-200 flex-1"></div>
                                    </div>
                                    <div className="text-sm font-black text-amber-600 uppercase tracking-widest ml-4">
                                        {group.totalPoints} {t.common.pts}
                                    </div>
                                </div>
                                <div className="grid gap-4">
                                    {group.quests.map((quest) => (
                                        <div key={quest.id} className="group bg-white rounded-2xl p-6 border-2 border-purple-50 hover:border-purple-400 transition-all shadow-sm flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center text-purple-600 font-black">
                                                    {quest.points}
                                                </div>
                                                <div>
                                                    <h3 className="font-black text-lg text-slate-800">{quest.title}</h3>
                                                    <div className="flex gap-2">
                                                        <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest">
                                                            {getFrequencyLabel(quest.frequency)}
                                                        </span>
                                                        {getDailyTimeLabel(quest) && (
                                                            <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                                                                {getDailyTimeLabel(quest)}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => completeQuest.mutate(quest.id)}
                                                disabled={isQuestDisabled(quest)}
                                                className="flex items-center gap-2 bg-purple-50 text-purple-600 px-6 py-3 rounded-xl font-black hover:bg-purple-600 hover:text-white transition-all transform active:scale-95 shadow-sm disabled:opacity-50 disabled:pointer-events-none"
                                            >
                                                <Circle size={20} className="group-hover:hidden" />
                                                <CheckCircle2 size={20} className="hidden group-hover:block" />
                                                Complete
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    // Fallback to flat list if grouping fails
                    <div className="grid gap-4">
                        {sortedActiveQuests?.map((quest) => (
                            <div key={quest.id} className="group bg-white rounded-2xl p-6 border-2 border-indigo-50 hover:border-indigo-400 transition-all shadow-sm flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600 font-black">
                                        {quest.points}
                                    </div>
                                    <div>
                                        <h3 className="font-black text-lg text-slate-800">{quest.title}</h3>
                                        <div className="flex gap-2">
                                            <span className="text-xs font-bold text-indigo-400 uppercase tracking-widest">
                                                {getFrequencyLabel(quest.frequency)}
                                            </span>
                                            {getQuestDayLabel(quest) && (
                                                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                                                    {getQuestDayLabel(quest)}
                                                </span>
                                            )}
                                            {getDailyTimeLabel(quest) && (
                                                <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">
                                                    {getDailyTimeLabel(quest)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => completeQuest.mutate(quest.id)}
                                    disabled={isQuestDisabled(quest)}
                                    className="flex items-center gap-2 bg-indigo-50 text-indigo-600 px-6 py-3 rounded-xl font-black hover:bg-indigo-600 hover:text-white transition-all transform active:scale-95 shadow-sm disabled:opacity-50 disabled:pointer-events-none"
                                >
                                    <Circle size={20} className="group-hover:hidden" />
                                    <CheckCircle2 size={20} className="hidden group-hover:block" />
                                    Complete
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Quest Approval Queue */}
            {((pendingApprovals?.length ?? 0) > 0 || missedUncompletedDaily.length > 0) && (
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-slate-500">
                        <Clock size={20} />
                        <h2 className="text-xl font-black uppercase tracking-tight">Quest Approval Queue</h2>
                    </div>

                    <div className="space-y-6">
                        {(missedApprovals.length > 0 || missedUncompletedDaily.length > 0) && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between px-1">
                                    <div className="flex items-center gap-2 flex-1">
                                        <div className="h-px bg-slate-200 flex-1"></div>
                                        <div className="text-sm font-black text-slate-600 uppercase tracking-widest px-3">Missed Quests</div>
                                        <div className="h-px bg-slate-200 flex-1"></div>
                                    </div>
                                </div>
                                <div className="grid gap-3">
                                    {missedUncompletedDaily.map((m) => (
                                        <div key={m.id} className="bg-rose-50 rounded-2xl p-5 border border-rose-200 flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-rose-500 font-bold border border-rose-100">
                                                    {m.quest?.points ?? 0}
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-slate-700">{m.quest?.title || 'Unknown Quest'}</h3>
                                                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                                                        Missed by {new Date(m.missed_deadline).toLocaleString()}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="text-[10px] font-bold text-rose-600 uppercase tracking-widest text-right">
                                                Not completed
                                            </div>
                                        </div>
                                    ))}
                                    {missedApprovals.map((c) => (
                                        <div key={c.id} className="bg-rose-50 rounded-2xl p-5 border border-rose-200 flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-rose-500 font-bold border border-rose-100">
                                                    {c.quest?.points ?? 0}
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-slate-700">{c.quest?.title || 'Unknown Quest'}</h3>
                                                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                                                        {new Date(c.completed_at).toLocaleString()}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => rejectCompletion.mutate(c.id)}
                                                    className="p-2 text-slate-300 hover:text-rose-500 transition-colors"
                                                    title={t.common.reject}
                                                >
                                                    <XCircle size={20} />
                                                </button>
                                                <button
                                                    onClick={() => approveCompletion.mutate(c.id)}
                                                    className="p-2 text-slate-300 hover:text-emerald-500 transition-colors"
                                                    title={t.common.approve}
                                                >
                                                    <CheckCircle2 size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {onTimeApprovals.length > 0 && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between px-1">
                                    <div className="flex items-center gap-2 flex-1">
                                        <div className="h-px bg-slate-200 flex-1"></div>
                                        <div className="text-sm font-black text-slate-600 uppercase tracking-widest px-3">Pending Approval</div>
                                        <div className="h-px bg-slate-200 flex-1"></div>
                                    </div>
                                </div>
                                <div className="grid gap-3">
                                    {onTimeApprovals.map((c) => (
                                        <div key={c.id} className="bg-white rounded-2xl p-5 border border-slate-200 flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-500 font-bold border border-slate-100">
                                                    {c.quest?.points ?? 0}
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-slate-700">{c.quest?.title || 'Unknown Quest'}</h3>
                                                    <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                                                        {new Date(c.completed_at).toLocaleString()}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => rejectCompletion.mutate(c.id)}
                                                    className="p-2 text-slate-300 hover:text-rose-500 transition-colors"
                                                    title={t.common.reject}
                                                >
                                                    <XCircle size={20} />
                                                </button>
                                                <button
                                                    onClick={() => approveCompletion.mutate(c.id)}
                                                    className="p-2 text-slate-300 hover:text-emerald-500 transition-colors"
                                                    title={t.common.approve}
                                                >
                                                    <CheckCircle2 size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Quest History - matching KidHome completed section style */}
            {history && history.length > 0 && (
                <div className="space-y-4">
                    <div className="flex items-center gap-2 text-slate-500">
                        <History size={20} />
                        <h2 className="text-xl font-black uppercase tracking-tight">Recently Done</h2>
                    </div>

                    <div className="space-y-6">
                        {groupedHistory.map((group) => (
                            <div key={group.key} className="space-y-3">
                                <div className="flex items-center justify-between px-1">
                                    <div className="flex items-center gap-2 flex-1">
                                        <div className="h-px bg-slate-200 flex-1"></div>
                                        <div className="text-sm font-black text-slate-600 uppercase tracking-widest px-3">
                                            {group.label}
                                        </div>
                                        <div className="h-px bg-slate-200 flex-1"></div>
                                    </div>
                                    <div className="text-sm font-black text-amber-600 uppercase tracking-widest ml-4">
                                        {group.totalPoints} {t.common.pts}
                                    </div>
                                </div>
                                <div className="grid gap-3 opacity-80">
                                    {group.items.map((item) => (
                                        <div key={item.id} className="bg-slate-50 rounded-2xl p-5 border border-slate-200 flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-slate-400 font-bold border border-slate-100">
                                                    {item.points || 0}
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-slate-600">
                                                        {item.type === 'manual_points'
                                                            ? item.title
                                                            : item.quest?.title || item.title || 'Unknown Quest'}
                                                    </h3>
                                                    <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest">
                                                        {item.type === 'manual_points' ? (
                                                            <span className="text-purple-500 flex items-center gap-1">
                                                                <Gift size={12} /> Bonus
                                                            </span>
                                                        ) : item.status === 'pending' ? (
                                                            <span className="text-amber-500 flex items-center gap-1">
                                                                <Clock size={12} /> Pending Approval
                                                            </span>
                                                        ) : item.status === 'approved' ? (
                                                            <span className="text-emerald-500 flex items-center gap-1">
                                                                <Check size={12} /> Approved
                                                            </span>
                                                        ) : (
                                                            <span className="text-rose-500">Rejected</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <div className="text-right">
                                                    <div className="text-[10px] font-bold text-slate-400 uppercase">
                                                        {new Date(item.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                    <div className="text-[10px] font-bold text-slate-400 uppercase">
                                                        {new Date(item.created_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                                                    </div>
                                                </div>
                                                {item.type !== 'manual_points' && item.status === 'pending' && (
                                                    <>
                                                        <button
                                                            onClick={() => rejectCompletion.mutate(item.id)}
                                                            className="p-2 text-slate-300 hover:text-rose-500 transition-colors"
                                                            title={t.common.reject}
                                                        >
                                                            <XCircle size={20} />
                                                        </button>
                                                        <button
                                                            onClick={() => approveCompletion.mutate(item.id)}
                                                            className="p-2 text-slate-300 hover:text-emerald-500 transition-colors"
                                                            title={t.common.approve}
                                                        >
                                                            <CheckCircle2 size={20} />
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        if (item.type === 'manual_points') {
                                                            if (confirm('Are you sure you want to delete this bonus entry?')) {
                                                                deleteBonus.mutate(item.id);
                                                            }
                                                            return;
                                                        }

                                                        if (confirm('Are you sure you want to delete this activity entry?')) {
                                                            deleteCompletion.mutate(item.id);
                                                        }
                                                    }}
                                                    className="p-2 text-slate-200 hover:text-red-500 transition-colors"
                                                    title={t.common.delete}
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
